import { client } from './client'
import { getCsrf, getCookie } from './authentication'
import { CustomError } from '../CustomError'
import { RawAssemblyTree } from '@/state'
import { CSRF_TOKEN_NAME } from '@/constants'

import type { CadVersion } from './types'

/**
 * Mark a CAD version as uploaded
 * @param cadVersionId
 * @returns
 */
export const cadVersionUploaded = async (cadVersionId: string) => {
  await getCsrf()
  return client.PUT('/api/v1/projects/cadversions/{cad_version_id}/uploaded', {
    headers: {
      'X-CSRFToken': getCookie(CSRF_TOKEN_NAME),
    },
    params: {
      path: {
        cad_version_id: cadVersionId,
      },
    },
  })
}

export const cadVersionUploadedAsync = async (cadVersionId: string) => {
  await getCsrf()
  return client.PUT(
    '/api/v1/projects/cadversions/{cad_version_id}/uploaded_async',
    {
      headers: {
        'X-CSRFToken': getCookie(CSRF_TOKEN_NAME),
      },
      params: {
        path: {
          cad_version_id: cadVersionId,
        },
      },
    },
  )
}

export const cadVersionProcessed = async (cadVersionId: string) => {
  await getCsrf()
  return client.PUT('/api/v1/projects/cadversions/{cad_version_id}/processed', {
    headers: {
      'X-CSRFToken': getCookie(CSRF_TOKEN_NAME),
    },
    params: {
      path: {
        cad_version_id: cadVersionId,
      },
    },
  })
}

/**
 * Get a CAD version
 * @param cadId
 * @returns
 */
export const listCadVersions = async (cadId: string) => {
  const resp = await client.GET('/api/v1/projects/{cad_id}/cadversions', {
    params: {
      path: {
        cad_id: cadId,
      },
    },
  })

  if (resp.error || !resp.response.ok) {
    const message = 'Failed to get CAD versions'
    throw new CustomError(resp, message)
  }

  return resp.data.sort(
    (a, b) => (b.version_number || 0) - (a.version_number || 0),
  ) as CadVersion[]
}

/**
 * Get a CAD version
 * @param cadVersionId
 * @returns
 */
export const getCadVersion = async (cadVersionId: string) => {
  const resp = await client.GET(
    '/api/v1/projects/cadversions/{cad_version_id}',
    {
      params: {
        path: {
          cad_version_id: cadVersionId,
        },
      },
    },
  )

  if (resp.error || !resp.response.ok) {
    const message = 'Failed to get CAD version'
    throw new CustomError(resp, message)
  }

  return resp.data as CadVersion
}

/**
 * Create a new CAD version
 * @param cadId
 * @param values
 * @returns
 */
export const createCadVersion = async (
  cadId: string,
  values: { filename: string; comment: string },
  jwtToken?: string | null,
) => {
  await getCsrf()

  const headers = {
    'X-CSRFToken': getCookie(CSRF_TOKEN_NAME),
  }

  if (jwtToken) {
    headers['Authorization'] = `Bearer ${jwtToken}`
  }

  const resp = await client.POST('/api/v1/projects/{cad_id}/cadversions', {
    headers,
    params: {
      path: {
        cad_id: cadId,
      },
    },
    body: values,
  })

  if (resp.error || !resp.response.ok) {
    const message = 'Failed to create CAD version'
    throw new CustomError(resp, message)
  }

  return resp.data as CadVersion
}

export const updateCadVersion = async (
  cadVersionId: string,
  values: { assembly_tree?: RawAssemblyTree | null; filename?: string },
) => {
  await getCsrf()
  const resp = await client.PUT(
    '/api/v1/projects/cadversions/{cad_version_id}',
    {
      headers: {
        'X-CSRFToken': getCookie(CSRF_TOKEN_NAME),
      },
      params: {
        path: {
          cad_version_id: cadVersionId,
        },
      },
      // @ts-expect-error - TODO: fix this
      body: values,
    },
  )

  if (resp.error || !resp.response.ok) {
    const message = 'Failed to update CAD version'
    throw new CustomError(resp, message)
  }

  return resp.data as CadVersion
}
